import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShipPosition } from "../types/ShipPosition";
import { useAuth } from "@clerk/react";
import { isAdminUser } from "../data/adminUsers";

type TopFuelConsumptionProps = {
  fuelConsumptionData: ShipPosition[] | null;
};

export default function TopFuelConsumption({ fuelConsumptionData }: TopFuelConsumptionProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const { isLoaded, has, userId } = useAuth();
  const isCaptain = isLoaded && (has({ plan: "vessel_vibes_captain" }) || isAdminUser(userId));

  const PAGE_SIZE = 3;
  const TOTAL_SHIPS = 10;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % Math.ceil(TOTAL_SHIPS / PAGE_SIZE));
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // When signed out, render locked state even without data
  if (!isCaptain) {
    return (
      <div className="bg-black/70 p-3 rounded-lg border border-blue-500 text-white text-xs w-64">
        <h4 className="font-semibold text-blue-400 mb-1 text-center">
          Global Fuel Rankings MT/h
        </h4>
        <ol className="list-decimal list-inside space-y-1">
          {[1, 2].map((n) => (
            <li key={n}>
              <span className="inline-block align-middle ml-1 w-36 h-3 bg-gray-600/70 rounded blur-[2px]" />
            </li>
          ))}
          <li style={{ listStyle: "none" }}>
            <a href="/captain" className="w-full text-left text-[11px] text-blue-400 hover:text-blue-300 transition-colors cursor-pointer">
              🔒 Captain Access to unlock
            </a>
          </li>
        </ol>
      </div>
    );
  }

  if (!fuelConsumptionData || fuelConsumptionData.length === 0) return null;

  const startIndex = currentPage * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, TOTAL_SHIPS);
  const visibleShips = fuelConsumptionData.slice(startIndex, endIndex);

  return (
    <div className="bg-black/70 p-3 rounded-lg border border-blue-500 text-white text-xs w-64">
      <h4 className="font-semibold text-blue-400 mb-1 text-center">
        Global Fuel Rankings MT/h
      </h4>

      <AnimatePresence mode="wait">
        <motion.ol
          key={currentPage}
          className="list-decimal list-inside space-y-1"
          start={startIndex + 1}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4 }}
        >
          {visibleShips.map((ship, index) => (
            <li key={`${ship.SHIPNAME}-${index}`}>
              {isCaptain ? (
                <>
                  <span className="font-bold">{ship.SHIPNAME.length > 21 ? ship.SHIPNAME.slice(0, 20) + '…' : ship.SHIPNAME}</span>
                  <span className="text-gray-300">: {ship.FUEL_CONSUMPTION}</span>
                </>
              ) : (
                <span className="inline-block align-middle ml-1 w-36 h-3 bg-gray-600/70 rounded blur-[2px]" />
              )}
            </li>
          ))}
          {!isCaptain && (
            <li style={{ listStyle: "none" }}>
              <a href="/captain" className="w-full text-left text-[11px] text-blue-400 hover:text-blue-300 transition-colors cursor-pointer">
                🔒 Captain Access to unlock
              </a>
            </li>
          )}
        </motion.ol>
      </AnimatePresence>
    </div>
  );
}
